import { api, unwrap, toPaginated, cleanParams } from "../lib/axios";
import type { ApiEnvelope, ListParams, PaginatedResult } from "../types/api";
import type {
  Driver,
  DriverAccountStatus,
  DriverDocumentType,
  DriverDocumentStatus,
  DocumentRejectionReason,
} from "../types/domain";

export interface DriverListParams extends ListParams {
  // Names match the backend GetAllDriversQueryDto.
  accountStatus?: string;
  vehicleCategoryID?: string;
  tripAvailability?: string;
  onboarding?: boolean;
  /** Tag key; unexpired tags only. */
  tag?: string;
}

export const driversApi = {
  async list(params: DriverListParams): Promise<PaginatedResult<Driver>> {
    const res = await api.get<ApiEnvelope<unknown>>("/driver/all", {
      params: cleanParams(params),
    });
    return toPaginated<Driver>(res, "drivers");
  },

  async get(id: string): Promise<Driver> {
    const res = await api.get<ApiEnvelope<Driver>>(`/driver/${id}`);
    return unwrap(res);
  },

  /**
   * Approve or reject one document. Approving the last outstanding document
   * moves the driver to UNDER_APPROVAL, not APPROVED — a vehicle category
   * still has to be assigned via approve().
   */
  async reviewDocument(
    id: string,
    docType: DriverDocumentType,
    body: {
      status: DriverDocumentStatus;
      rejectionReason?: DocumentRejectionReason;
      rejectionComment?: string;
      /** Licence only; required to approve a licence with no expiry on file. */
      licenseExpiryDate?: string;
    },
  ): Promise<Driver> {
    const res = await api.patch<ApiEnvelope<{ driver: Driver }>>(
      `/admin/drivers/${id}/documents/${docType}/review`,
      body,
    );
    return unwrap(res).driver;
  },

  /**
   * Approving a driver also stamps the licence expiry (DD-MM-YYYY) and the
   * LATRA details: the driver no longer enters them, and the backend refuses
   * to approve without them.
   */
  async approve(
    id: string,
    vehicleCategoryID: string,
    reason?: string,
    licenseExpiryDate?: string,
    latra?: {
      registrationNumber: string;
      licenseNumber: string;
    },
  ): Promise<Driver> {
    const res = await api.patch<ApiEnvelope<{ driver: Driver }>>(
      `/admin/approve-driver/${id}`,
      { vehicleCategoryID, reason, licenseExpiryDate, ...latra },
    );
    return unwrap(res).driver;
  },

  async setAccountStatus(
    id: string,
    status: DriverAccountStatus,
    reason?: string,
  ): Promise<Driver> {
    const res = await api.patch<ApiEnvelope<Driver>>(
      `/driver/${id}/account-status`,
      { status, reason },
    );
    return unwrap(res);
  },

  async setGracePeriod(id: string, body: Record<string, unknown>): Promise<void> {
    await api.post(`/driver/${id}/grace-period`, body);
  },

  async updateVehicleDetails(
    id: string,
    body: Record<string, unknown>,
  ): Promise<Driver> {
    const res = await api.patch<ApiEnvelope<Driver>>(
      `/driver/${id}/vehicle-details`,
      body,
    );
    return unwrap(res);
  },

  async update(id: string, body: Record<string, unknown>): Promise<Driver> {
    const res = await api.patch<ApiEnvelope<Driver>>(`/driver/${id}`, body);
    return unwrap(res);
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/driver/${id}`);
  },
};
